<template>
  <div class="mt-6">
    <div class="mb-3 flex items-center justify-between">
      <div class="flex items-center space-x-2">
        <div
          class="flex h-9 w-9 items-center justify-center rounded-lg bg-yellowLight text-primary"
        >
          <i class="fas fa-shield-alt" />
        </div>
        <div>
          <div class="text-sm font-medium leading-5 text-grey">
            Доверенные устройства
          </div>
          <div v-if="devices.length" class="text-xs leading-4 text-gray-400">
            {{ devices.length }} {{ pluralize }}
          </div>
        </div>
      </div>
      <button
        @click="loadDevices"
        class="flex h-9 w-9 items-center justify-center rounded-lg bg-greyLight text-grey active:scale-95"
        :aria-label="'Обновить список устройств'"
      >
        <i
          class="fas fa-sync-alt transition-transform duration-300"
          :class="{ 'animate-spin': loading }"
        />
      </button>
    </div>

    <div v-if="loading" class="space-y-3">
      <div
        v-for="i in 3"
        :key="i"
        class="h-[86px] animate-pulse rounded-md bg-gray-200"
      />
    </div>

    <div
      v-else-if="error"
      class="flex flex-col items-center rounded-md bg-greyLight px-5 py-8 text-center"
    >
      <i class="fas fa-exclamation-triangle text-2xl text-red" />
      <div class="mt-3 text-sm text-grey">
        Не удалось загрузить список устройств
      </div>
      <button-component
        color="primary"
        rounded="lg"
        text-color="white"
        title="Повторить"
        height="10"
        class="mt-4 w-1/2"
        @clickAction="loadDevices"
      />
    </div>

    <div
      v-else-if="!devices.length"
      class="flex flex-col items-center rounded-md bg-greyLight px-5 py-8 text-center"
    >
      <i class="fas fa-mobile-alt text-3xl text-gray-300" />
      <div class="mt-3 text-sm font-medium text-grey">
        Нет доверенных устройств
      </div>
      <div class="mt-1 text-xs text-gray-400">
        Устройства, с которых вы входили в приложение, появятся здесь.
      </div>
    </div>

    <div v-else class="space-y-3">
      <div
        v-for="device in devices"
        :key="device.id"
        class="overflow-hidden rounded-md bg-greyLight"
        :class="
          device.is_current
            ? 'ring-2 ring-blue'
            : device.has_active_session
            ? 'ring-2 ring-green'
            : ''
        "
      >
        <button
          class="flex w-full items-center px-4 py-3 text-left active:bg-gray-100"
          @click="toggleExpand(device.id)"
        >
          <div
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white"
            :class="platformStyle(device.platform).bg"
          >
            <i :class="platformStyle(device.platform).icon" class="text-lg" />
          </div>

          <div class="ml-3 min-w-0 flex-1">
            <div class="flex items-center justify-between space-x-2">
              <div class="flex min-w-0 items-baseline space-x-2">
                <span
                  class="truncate text-sm font-semibold leading-5 text-grey"
                >
                  {{ platformTitle(device.platform) }}
                </span>
                <span
                  v-if="device.app_version && isNativePlatform(device)"
                  class="shrink-0 text-xs leading-5 text-gray-400"
                >
                  {{ device.app_version }}
                </span>
              </div>
              <span
                v-if="device.is_current"
                class="shrink-0 rounded-full bg-blue px-2 py-0.5 text-[10px] font-semibold leading-none text-white"
              >
                <span
                  class="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-white"
                />
                Это устройство
              </span>
              <span
                v-else-if="device.has_active_session"
                class="flex shrink-0 items-center space-x-1 rounded-full bg-green px-2 py-0.5 text-[10px] font-semibold leading-none text-white"
              >
                <span
                  class="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-white"
                />
                <span>Сессия активна</span>
              </span>
            </div>
            <div class="mt-1 flex items-center space-x-2">
              <span class="truncate text-xs text-gray-400">
                {{
                  device.city_locator ||
                  device.title_locator ||
                  "Местоположение неизвестно"
                }}
              </span>
              <span
                v-if="device.locator_pending"
                role="button"
                @click.stop="requestLocation(device)"
                class="shrink-0 cursor-pointer rounded-full bg-blue px-2 py-0.5 text-[10px] font-semibold leading-none text-white active:scale-95"
              >
                Запросить местоположение
              </span>
            </div>
            <div class="mt-0.5 truncate text-xs text-gray-400">
              {{ device.ip_address || "—" }},
              {{ device.user_agent || "—" }}
            </div>
          </div>

          <i
            class="fas fa-chevron-down ml-2 text-gray-400 transition-transform duration-300"
            :class="{ 'rotate-180': expandedId === device.id }"
          />
        </button>

        <div v-auto-animate="{ duration: 250 }">
          <div
            v-if="expandedId === device.id"
            class="border-t border-gray-200 px-4 py-3"
          >
            <div class="grid grid-cols-2 gap-y-2 text-xs">
              <div class="text-gray-400">IP-адрес</div>
              <div class="text-right font-medium text-grey">
                {{ device.ip_address || "—" }}
              </div>
              <div class="text-gray-400">Браузер</div>
              <div class="text-right font-medium text-grey">
                {{ device.user_agent || "—" }}
              </div>
              <div class="text-gray-400">Версия приложения</div>
              <div class="text-right font-medium text-grey">
                {{ isNativePlatform(device) ? device.app_version || "—" : platformTitle(device.platform) }}
              </div>
              <div class="text-gray-400">Местоположение</div>
              <div class="text-right font-medium text-grey">
                {{ device.title_locator || "—" }}
              </div>
              <div
                v-if="device.locator_pending"
                class="col-span-2 flex justify-end"
              >
                <button-component
                  color="blue"
                  rounded="lg"
                  text-color="white"
                  title="Запросить местоположение"
                  height="10"
                  :loading="requestingLocationId === device.id"
                  @clickAction="requestLocation(device)"
                />
              </div>
              <div
                v-else-if="!device.city_locator"
                class="col-span-2 flex items-start space-x-1.5 text-red"
              >
                <i class="fas fa-exclamation-circle mt-0.5" />
                <span>Точное местоположение определить не удалось</span>
              </div>
              <div class="col-span-2 flex items-start space-x-1.5 text-red">
                <i class="fas fa-exclamation-circle mt-0.5" />
                <span>
                  Данные о местоположении предоставляет провайдер <br />
                  (могут отличаться от реальных)
                </span>
              </div>
              <div class="text-gray-400">Последний вход</div>
              <div class="text-right font-medium text-grey">
                {{ formatDate(device.last_seen_at) }}
              </div>
              <!-- <div class="text-gray-400">Действует до</div>
              <div class="text-right font-medium text-grey">
                {{ formatDate(device.expires_at) }}
              </div> -->
            </div>

            <div v-if="!device.is_current" class="mt-4 flex flex-col space-y-3">
              <button-component
                v-if="device.has_active_session"
                color="blue"
                rounded="lg"
                text-color="white"
                title="Завершить сессию"
                height="10"
                :loading="deauthorizingId === device.id"
                @clickAction="deauthorizeDevice(device)"
              />
              <button-component
                color="red"
                rounded="lg"
                text-color="white"
                title="Отозвать доступ"
                height="10"
                :loading="deletingId === device.id"
                @clickAction="deleteDevice(device)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <status-modal
      type="success"
      :state="statusModalDialog"
      :description="statusModalText"
      button-text="Ок"
      @confirm="statusModalDialog = false"
    />
  </div>
</template>

<script lang="ts">
import ApiEnter from "@/api/ApiEnter";
import ButtonComponent from "@/components/Common/ButtonComponent.vue";
import StatusModal from "@/components/Modals/StatusModal.vue";
import { EDevicePlatform, ITrustedDevice } from "@/struct/IDevice/IDevice";
import UtilError from "@/util/ValidateError/UtilError";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Options, Vue } from "vue-class-component";

dayjs.extend(utc);

@Options({
  components: { ButtonComponent, StatusModal },
})
export default class TrustedDevices extends Vue {
  private devices: ITrustedDevice[] = [];
  private loading = false;
  private error = false;
  private expandedId: string | null = null;
  private deauthorizingId: string | null = null;
  private deletingId: string | null = null;
  private requestingLocationId: string | null = null;
  private statusModalDialog = false;
  private statusModalText = "";
  private refreshTimer: number | null = null;

  private get pluralize(): string {
    const count = this.devices.length;
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11) return "устройство";
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14))
      return "устройства";
    return "устройств";
  }

  private toggleExpand(id: string) {
    this.expandedId = this.expandedId === id ? null : id;
  }

  private async loadDevices(showLoader = true) {
    if (this.loading) return;
    this.loading = true;
    if (showLoader) this.error = false;
    try {
      const response = await ApiEnter.getTrustedDevices();
      if (Array.isArray(response)) {
        this.devices = this.sortDevices(response);
        this.error = false;
      } else if (showLoader) {
        this.error = true;
      }
    } catch (e) {
      if (showLoader) this.error = true;
    } finally {
      this.loading = false;
    }
  }

  private async deauthorizeDevice(device: ITrustedDevice) {
    if (this.deauthorizingId) return;
    this.deauthorizingId = device.id;
    try {
      const response = await ApiEnter.deauthorizeDevice(device.id);
      if (response?.detail === "OK") {
        this.statusModalText = "Устройство деавторизовано";
        this.statusModalDialog = true;
        this.expandedId = null;
        await this.loadDevices();
      } else if (!UtilError.showBackendErrorBody(response)) {
        UtilError.showBackendError(
          undefined,
          undefined,
          undefined,
          undefined,
          "Не удалось деавторизовать устройство. Попробуйте позже",
        );
      }
    } finally {
      this.deauthorizingId = null;
    }
  }

  private async requestLocation(device: ITrustedDevice) {
    if (this.requestingLocationId) return;
    this.requestingLocationId = device.id;
    try {
      const response = await ApiEnter.requestDeviceLocations([device.id]);
      if (response?.detail === "OK") {
        this.statusModalText =
          "Запрос на определение местоположения отправлен";
        this.statusModalDialog = true;
        await this.loadDevices();
      } else if (!UtilError.showBackendErrorBody(response)) {
        UtilError.showBackendError(
          undefined,
          undefined,
          undefined,
          undefined,
          "Не удалось запросить местоположение. Попробуйте позже",
        );
      }
    } finally {
      this.requestingLocationId = null;
    }
  }

  private async deleteDevice(device: ITrustedDevice) {
    if (this.deletingId) return;
    this.deletingId = device.id;
    try {
      const response = await ApiEnter.deleteDevice(device.id);
      if (response?.detail === "OK") {
        this.statusModalText =
          "Доступ устройства отозван, для входа потребуется подтверждение";
        this.statusModalDialog = true;
        this.expandedId = null;
        await this.loadDevices();
      } else if (!UtilError.showBackendErrorBody(response)) {
        UtilError.showBackendError(
          undefined,
          undefined,
          undefined,
          undefined,
          "Не удалось удалить устройство. Попробуйте позже",
        );
      }
    } finally {
      this.deletingId = null;
    }
  }

  private sortDevices(devices: ITrustedDevice[]): ITrustedDevice[] {
    return [...devices].sort((a, b) => {
      const rankA = this.deviceRank(a);
      const rankB = this.deviceRank(b);
      if (rankA !== rankB) return rankA - rankB;

      const timeA = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0;
      const timeB = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0;
      return timeB - timeA;
    });
  }

  private deviceRank(device: ITrustedDevice): number {
    if (device.is_current) return 0;
    if (device.has_active_session) return 1;
    return 2;
  }

  private isNativePlatform(device: ITrustedDevice): boolean {
    return (
      device.platform === EDevicePlatform.Android ||
      device.platform === EDevicePlatform.Ios
    );
  }

  private platformTitle(platform: EDevicePlatform): string {
    switch (platform) {
      case EDevicePlatform.Web:
        return "Веб-браузер";
      case EDevicePlatform.Pwa:
        return "PWA";
      case EDevicePlatform.Android:
        return "Android";
      case EDevicePlatform.Ios:
        return "iOS";
      case EDevicePlatform.Telegram:
        return "Telegram";
      case EDevicePlatform.WebSocket:
        return "WebSocket";
      default:
        return platform;
    }
  }

  private platformStyle(platform: EDevicePlatform): {
    icon: string;
    bg: string;
  } {
    switch (platform) {
      case EDevicePlatform.Web:
        return { icon: "fas fa-globe", bg: "bg-blue" };
      case EDevicePlatform.Pwa:
        return { icon: "fas fa-mobile-alt", bg: "bg-primary" };
      case EDevicePlatform.Android:
        return { icon: "fab fa-android", bg: "bg-green" };
      case EDevicePlatform.Ios:
        return { icon: "fab fa-apple", bg: "bg-grey" };
      case EDevicePlatform.Telegram:
        return { icon: "fab fa-telegram-plane", bg: "bg-blue" };
      case EDevicePlatform.WebSocket:
        return { icon: "fas fa-plug", bg: "bg-blue" };
      default:
        return { icon: "fas fa-laptop", bg: "bg-darkGrey" };
    }
  }

  private formatDate(date: string | null): string {
    if (!date) return "—";
    return dayjs.utc(date).local().format("DD.MM.YYYY HH:mm");
  }

  created() {
    this.loadDevices();
  }

  mounted() {
    this.refreshTimer = window.setInterval(() => {
      this.loadDevices(false);
    }, 60 * 1000);
  }

  unmounted() {
    if (this.refreshTimer !== null) {
      window.clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}
</script>
